import { useState, useCallback, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  Search,
  Loader2,
  CheckCircle,
  AlertCircle,
  Link2,
  ExternalLink,
  Import,
  X
} from "lucide-react";
import { cn } from "@/lib/utils";

interface GuestSalesNavImporterProps {
  clientId: string;
  clientName?: string;
  onImport: (targets: any[], headers: string[]) => void;
  uploadedCount: number;
  guestToken?: string;
}

interface SearchResult {
  provider_id: string | null;
  profile_url: string | null;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  title: string | null;
  headline: string | null;
  location: string | null;
  profile_picture_url: string | null;
  link_type: string;
}

const RESULTS_PER_PAGE = 25;
const MAX_PAGES = 100;

export const GuestSalesNavImporter = ({
  clientId,
  clientName,
  onImport,
  uploadedCount,
  guestToken,
}: GuestSalesNavImporterProps) => {
  const [salesNavUrl, setSalesNavUrl] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [pagesLoaded, setPagesLoaded] = useState(0);

  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isCancelledRef = useRef(false);

  const isValidSalesNavUrl = useCallback((url: string): boolean => {
    return /linkedin\.com\/sales\/(search\/people|lists\/people\/\d+)/.test(url);
  }, []);

  const executeSearchRequest = async (
    searchCursor?: string,
    startOffset?: number
  ): Promise<{
    items: SearchResult[];
    cursor: string | null;
    total_count: number | null;
    has_more: boolean;
  }> => {
    // For guests, we need to use the guest token RPC pattern
    // But linkedin-search edge function doesn't support guest tokens yet
    // So we'll invoke it directly - the edge function needs the client's unipile_account_id
    
    const response = await supabase.functions.invoke("linkedin-search", {
      body: {
        url: salesNavUrl,
        clientId: clientId,
        cursor: searchCursor,
        limit: RESULTS_PER_PAGE,
        start_offset: startOffset,
        // Pass guest token for auth context if needed
        guestToken: guestToken,
      },
    });

    if (response.error) {
      throw new Error(response.error.message || "Search failed");
    }

    const data = response.data;

    if (!data.success) {
      throw new Error(data.error || "Search failed");
    }

    return {
      items: data.items,
      cursor: data.cursor,
      total_count: data.total_count,
      has_more: data.has_more,
    };
  };

  const executeSearch = async () => {
    if (!salesNavUrl) {
      toast.error("Please enter a Sales Navigator search or lead list URL");
      return;
    }

    if (!isValidSalesNavUrl(salesNavUrl)) {
      setError("Please paste a Sales Navigator People search or saved lead list URL");
      return;
    }

    setError(null);
    setIsSearching(true);
    setSearchResults([]);
    setCursor(null);
    setTotalCount(null);
    setHasMore(false);
    setHasSearched(true);
    setPagesLoaded(0);

    try {
      const result = await executeSearchRequest();

      setSearchResults(result.items);
      setCursor(result.cursor);
      setTotalCount(result.total_count);
      setHasMore(result.has_more);
      setPagesLoaded(1);

      if (result.items.length === 0) {
        toast.info("No results found for this search");
      } else {
        toast.success(`Found ${result.total_count?.toLocaleString() || result.items.length} targets`);
      }
    } catch (err: any) {
      console.error("[GuestSalesNavImporter] Search error:", err);
      setError(err.message || "Failed to search. Please try again.");
      toast.error(err.message || "Search failed");
    } finally {
      setIsSearching(false);
    }
  };

  const loadMore = async () => {
    if (!cursor || !hasMore || isLoadingMore) return;

    setIsLoadingMore(true);
    isCancelledRef.current = false;

    try {
      const result = await executeSearchRequest(cursor);

      if (!isCancelledRef.current) {
        setSearchResults(prev => [...prev, ...result.items]);
        setCursor(result.cursor);
        setHasMore(result.has_more);
        setPagesLoaded(prev => prev + 1);
      }
    } catch (err: any) {
      if (!isCancelledRef.current) {
        toast.error(err.message || "Failed to load more results");
      }
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleImport = () => {
    if (searchResults.length === 0) {
      toast.error("No results to import");
      return;
    }

    // Deduplicate by profile_url
    const seenUrls = new Set<string>();
    const uniqueResults = searchResults.filter(result => {
      if (!result.profile_url) return false;
      const normalized = result.profile_url.toLowerCase();
      if (seenUrls.has(normalized)) return false;
      seenUrls.add(normalized);
      return true;
    });

    // Transform to target format
    const targets = uniqueResults.map(result => ({
      linkedin_url: result.profile_url,
      profile_url: result.profile_url,
      sales_nav_url: salesNavUrl,
      link_type: "sales_nav",
      provider_id: result.provider_id,
      first_name: result.first_name,
      last_name: result.last_name,
      company: result.company,
      title: result.title,
      name: result.first_name && result.last_name
        ? `${result.first_name} ${result.last_name}`
        : (result.first_name || result.last_name || null),
      custom_data: null,
    }));

    const headers = ["first_name", "last_name", "company", "title"];
    onImport(targets, headers);
    toast.success(`Imported ${targets.length} targets`);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isSearching && salesNavUrl) {
      executeSearch();
    }
  };

  // If already imported, show summary
  if (uploadedCount > 0 && searchResults.length === 0) {
    return (
      <Card className="p-6 border-2 border-emerald-500/30 bg-emerald-500/5">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-full bg-emerald-500/20">
            <CheckCircle className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h4 className="font-semibold text-lg text-emerald-900 dark:text-emerald-100">
              {uploadedCount} Targets Imported
            </h4>
            <p className="text-sm text-muted-foreground">
              From LinkedIn Sales Navigator search
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="ml-auto"
            onClick={() => {
              setSearchResults([]);
              setSalesNavUrl("");
              setHasSearched(false);
            }}
          >
            Import Different
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="p-6 border-2 border-dashed border-border/60 bg-muted/10">
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <Link2 className="w-5 h-5 text-primary" />
            <h4 className="font-semibold">Import from Sales Navigator</h4>
          </div>

          <p className="text-sm text-muted-foreground">
            Paste your Sales Navigator People search or saved lead list URL to import leads using{" "}
            <span className="font-medium">{clientName || "your connected account"}</span>.
          </p>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-3">
            <div>
              <Label htmlFor="salesnav-url" className="text-sm font-medium">
                Sales Navigator Search URL
              </Label>
              <Input
                id="salesnav-url"
                placeholder="https://www.linkedin.com/sales/search/people?..."
                value={salesNavUrl}
                onChange={(e) => setSalesNavUrl(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isSearching}
                className={cn("mt-1.5 bg-background", error && "border-destructive")}
              />
            </div>

            <Button
              onClick={executeSearch}
              disabled={isSearching || !salesNavUrl}
              className="w-full"
            >
              {isSearching ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>

      {/* Results */}
      {hasSearched && searchResults.length > 0 && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Badge variant="secondary" className="text-sm">
                {searchResults.length} results loaded
              </Badge>
              {totalCount && (
                <span className="text-sm text-muted-foreground">
                  of {totalCount.toLocaleString()} total
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              {hasMore && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadMore}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? (
                    <>
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    "Load More"
                  )}
                </Button>
              )}

              <Button onClick={handleImport} size="sm">
                <Import className="w-3 h-3 mr-1" />
                Import {searchResults.length} Targets
              </Button>
            </div>
          </div>

          <div className="max-h-64 overflow-auto border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {searchResults.slice(0, 50).map((result, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">
                      {result.first_name} {result.last_name}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground truncate max-w-[200px]">
                      {result.title || "-"}
                    </TableCell>
                    <TableCell className="text-sm truncate max-w-[150px]">
                      {result.company || "-"}
                    </TableCell>
                    <TableCell>
                      {result.profile_url && (
                        <a
                          href={result.profile_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground hover:text-primary"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {searchResults.length > 50 && (
              <div className="text-center py-2 text-xs text-muted-foreground bg-muted/50">
                Showing first 50 of {searchResults.length} results
              </div>
            )}
          </div>
        </Card>
      )}

      {hasSearched && searchResults.length === 0 && !isSearching && (
        <Card className="p-6 text-center text-muted-foreground">
          <p>No results found. Try adjusting your search criteria in Sales Navigator.</p>
        </Card>
      )}
    </div>
  );
};
